import { useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from '../components/ui';
import { countIdeas, FLUENCY_SECONDS, fluencyTarget, ideaKey, makeFluencyRound } from './fluency';
import { elapsed, type DrillProps } from './meta';

/** 주제 2개, 주제마다 90초 — 한 줄에 하나씩 적고 Enter. 시간이 끝나거나 '다 했어요'를 누르면 다음 주제 */
export function FluencyDrill({ level, seed, onDone }: DrillProps) {
  const prompts = useMemo(() => makeFluencyRound(seed), [seed]);
  const target = fluencyTarget(level);
  const started = useRef(performance.now());
  const asked = useRef(performance.now());
  const done = useRef<{ prompt: string; ideas: string[] }[]>([]);
  const [index, setIndex] = useState(0);
  const [ideas, setIdeas] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [left, setLeft] = useState(1);
  const [dup, setDup] = useState(false);

  const finish = () => {
    const record = [...done.current, { prompt: prompts[index][1], ideas }];
    done.current = record;
    if (index + 1 >= prompts.length) {
      const counts = record.map((r) => countIdeas(r.ideas));
      onDone({
        correct: counts.reduce((s, c) => s + Math.min(c, target), 0),
        total: target * record.length,
        duration_ms: elapsed(started.current),
        detail: { seed, target, rounds: record.map((r, i) => ({ prompt: r.prompt, count: counts[i], ideas: r.ideas })) },
        summary: [
          ...record.map((r, i) => `"${r.prompt}" — ${counts[i]}개 (목표 ${target}개)`),
          '요령: 생각을 범주로 바꿔 가며 짜내세요 (재료로 · 도구로 · 장식으로 · 놀이로 · 돈벌이로 …). 막히면 "누가 / 어디서 / 언제" 를 바꿔 봅니다.',
        ],
      });
      return;
    }
    asked.current = performance.now();
    setIdeas([]);
    setText('');
    setLeft(1);
    setIndex(index + 1);
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  useEffect(() => {
    const id = window.setInterval(() => {
      const remain = 1 - (performance.now() - asked.current) / (FLUENCY_SECONDS * 1000);
      setLeft(Math.max(0, remain));
      if (remain <= 0) finishRef.current();
    }, 200);
    return () => window.clearInterval(id);
  }, [index]);

  const add = () => {
    const t = text.trim();
    if (!t) return;
    if (ideas.some((x) => ideaKey(x) === ideaKey(t))) {
      setDup(true);
      return;
    }
    setDup(false);
    setIdeas([...ideas, t]);
    setText('');
  };

  const count = countIdeas(ideas);
  return (
    <div className="drill fluency-drill">
      <div className="row-between">
        <span className="drill-progress">주제 {index + 1} / {prompts.length}</span>
        <span className="small muted">{Math.ceil(left * FLUENCY_SECONDS)}초</span>
      </div>
      <Bar value={left} tone={left < 0.2 ? 'warn' : 'accent'} />
      <p className="fluency-kind small muted">{prompts[index][0]}</p>
      <p className="lang-ask">{prompts[index][1]}</p>
      <form className="calc-form fluency-form" onSubmit={(e) => { e.preventDefault(); add(); }}>
        <input
          key={index} className="input" autoFocus autoComplete="off" aria-label="아이디어" placeholder="떠오르는 대로 적고 Enter"
          value={text} onChange={(e) => { setText(e.target.value); setDup(false); }} enterKeyHint="next"
        />
        <button type="submit" className="btn" disabled={!text.trim()}>추가</button>
      </form>
      <p className={`small ${count >= target ? 'fluency-hit' : 'muted'}`} aria-live="polite">
        {dup ? '이미 적은 아이디어입니다' : `${count}개 / 목표 ${target}개`}
      </p>
      <ol className="fluency-list">
        {ideas.map((x, i) => (
          <li key={`${i}-${x}`}>
            {x}
            <button type="button" className="link small" aria-label={`${x} 지우기`} onClick={() => setIdeas(ideas.filter((_, j) => j !== i))}>×</button>
          </li>
        ))}
      </ol>
      <div className="drill-end">
        <button type="button" className="btn btn-primary" onClick={finish}>{index + 1 >= prompts.length ? '다 했어요 — 결과 보기' : '다 했어요 — 다음 주제'}</button>
      </div>
    </div>
  );
}
